const DENSITY = {
    // regular = the A4 invoice items size
    regular: { cell: '12px', head: '10px', padX: '4px' },
    compact: { cell: '11px', head: '9px', padX: '3px' },
    dense: { cell: '10px', head: '8.5px', padX: '2px', stretch: '75%' },
};
const RULE = '1px solid var(--print-color, #000000)';
const ROW_RULE = '0.5px solid #b4b4b4';
const TEXT = { fontSize: '11px', lineHeight: '1.3' };
const NOWRAP = ['index', 'code', 'figure'];
/** Left padding of an amount. Where the heading already makes the column wider it adds no width. */
const FIGURE_GAP = '6px';
/** A wrapping name keeps a line of this many characters, or its longest line when shorter. */
const NAME_FLOOR = 18;
/** ch per character: `ch` is the width of "0", and capitals run about 3 % wider, so 18 characters → 20ch (≈ 99 px at the dense size). */
const CH_PER_CHARACTER = 1.1;
/** The heading's box asks the column for its longest word only, then fills the column and wraps there. */
const HEADING_BOX = { width: 'min-content', minWidth: '100%' };
function nameFloor(rows, index) {
    const longest = Math.max(0, ...rows.flatMap(row => [row[index] ?? ''].flat().map(line => line.length)));
    return longest ? { minWidth: `${Math.ceil(Math.min(longest, NAME_FLOOR) * CH_PER_CHARACTER)}ch` } : {};
}
function gapOf(column) {
    return column.kind === 'figure' ? { paddingLeft: FIGURE_GAP } : {};
}
function stretchOf(density) {
    const stretch = DENSITY[density].stretch;
    return stretch ? { fontStretch: stretch } : {};
}
function alignOf(column) {
    return column.kind === 'figure' ? 'R' : 'L';
}
function cellStyle(column, density) {
    const d = DENSITY[density];
    return {
        fontSize: d.cell,
        padding: `1px ${d.padX}`,
        ...gapOf(column),
        lineHeight: '1.2',
        verticalAlign: 'top',
        borderBottom: ROW_RULE,
        ...stretchOf(density),
        ...(NOWRAP.includes(column.kind) ? { whiteSpace: 'nowrap' } : {}),
    };
}
/** "Label: value" pairs on one line; pairs without a value are left out. */
export function factLine(...pairs) {
    return pairs
        .filter((pair) => !!pair && pair[1] !== null && pair[1] !== undefined && pair[1] !== '')
        .map(([label, value]) => `${label}: ${value}`)
        .join('     ');
}
/** Title band: title + venue on the first line, document facts + venue tax id on the second, one rule under it. */
export function documentHeader(pr, header) {
    pr.table({ colNumber: 2, html: { style: { width: '100%' } } });
    pr.tl({ content: header.title, style: { fontSize: '16px', fontWeight: '700', lineHeight: '1.2', padding: '0' } }, { content: header.venueName, align: 'R', style: { fontSize: '12px', fontWeight: '600', lineHeight: '1.2', padding: '0', whiteSpace: 'nowrap', verticalAlign: 'bottom' } });
    pr.tl({ content: header.facts, style: { ...TEXT, padding: '2px 0 5px', borderBottom: RULE } }, { content: header.venueLine, align: 'R', style: { ...TEXT, padding: '2px 0 5px', borderBottom: RULE, whiteSpace: 'nowrap', verticalAlign: 'bottom' } });
}
/**
 * Header of a prescribed form (KEP): left / right lines first, then the centred title and subtitle —
 * the form's own order.
 */
export function documentFormHeader(pr, header) {
    pr.table({ colNumber: 2, html: { style: { width: '100%' } } });
    header.lines.forEach(([left, right]) => pr.tl({ content: left, style: { ...TEXT, padding: '0' } }, { content: right, align: 'R', style: { ...TEXT, padding: '0', whiteSpace: 'nowrap' } }));
    pr.l('L', header.title, { align: 'C', style: { fontSize: '16px', fontWeight: '700', lineHeight: '1.2', marginTop: '8px' } });
    pr.l('L', header.subtitle, { align: 'C', style: { ...TEXT, marginBottom: '8px' } });
}
/** Further header lines (counterparty, transport, balance…) under the title band. */
export function documentLines(pr, lines) {
    const shown = lines.filter(Boolean);
    shown.forEach((line, index) => pr.l('L', line, {
        style: { ...TEXT, marginTop: index === 0 ? '4px' : '0', marginBottom: index === shown.length - 1 ? '6px' : '0' },
    }));
}
/**
 * Two header blocks side by side: lines that may wrap on the left, label / value pairs on the right
 * (the kalkulacija's supplier invoice | Sumarno).
 */
export function documentBlocks(pr, blocks) {
    const box = { ...TEXT, padding: '0', verticalAlign: 'top' };
    const title = { ...box, fontWeight: '700', paddingBottom: '2px' };
    documentSpacer(pr, 5);
    pr.table({ colNumber: 3, html: { style: { width: '100%' } } });
    pr.tl({ content: blocks.left.title, style: title }, { content: blocks.right.title, colSpan: 2, align: 'R', style: title });
    pr.tl({ content: blocks.left.lines.filter(Boolean), style: { ...box, width: '100%', paddingRight: '24px' } }, { content: blocks.right.pairs.map(([label]) => `${label}:`), align: 'R', style: { ...box, whiteSpace: 'nowrap', paddingRight: '8px' } }, { content: blocks.right.pairs.map(([, value]) => value), align: 'R', style: { ...box, whiteSpace: 'nowrap' } });
}
/** A fixed vertical gap; the default is the gap between a header and its items table. */
export function documentSpacer(pr, px = 8) {
    pr.l('L', '', { style: { minHeight: `${px}px` } });
}
/** Items table: heading row with a rule, one row per item with a light rule, optional totals and recap. */
export function documentTable(pr, columns, rows, options) {
    const { density, totals, recap } = options;
    const d = DENSITY[density];
    // PrintableHtml fills table rows by cell count, so a short totals row would silently shift its
    // values under the wrong columns.
    if (totals && totals.span + totals.values.length !== columns.length) {
        throw new Error(`documentTable: totals span ${totals.span} + ${totals.values.length} values ≠ ${columns.length} columns`);
    }
    if (recap && columns.length < 4) {
        throw new Error('documentTable: a recap needs at least 4 columns');
    }
    pr.table({ colNumber: columns.length, html: { style: { width: '100%' } } });
    pr.tl(...columns.map((column) => ({
        content: column.label,
        align: alignOf(column),
        style: {
            fontSize: d.head,
            fontWeight: '600',
            lineHeight: '1.15',
            verticalAlign: 'bottom',
            padding: `0 ${d.padX} 3px`,
            borderBottom: RULE,
            ...stretchOf(density),
        },
        boxStyle: HEADING_BOX,
    })));
    // on the item cells: `ch` follows the font size, and the heading's is smaller
    const floors = columns.map((column, index) => column.kind === 'name' ? nameFloor(rows, index) : {});
    rows.forEach(row => pr.tl(...row.map((value, index) => ({
        content: value,
        align: alignOf(columns[index]),
        style: { ...cellStyle(columns[index], density), ...floors[index] },
    }))));
    if (totals) {
        const strong = { fontSize: d.cell, padding: `1px ${d.padX}`, fontWeight: '700', lineHeight: '1.2', borderTop: RULE, whiteSpace: 'nowrap', ...stretchOf(density) };
        pr.tl({ content: totals.label, colSpan: totals.span, align: 'R', style: strong }, ...totals.values.map((value, index) => ({ content: value, align: 'R', style: { ...strong, ...gapOf(columns[totals.span + index]) } })));
    }
    if (recap) {
        const plain = { fontSize: d.cell, padding: `1px ${d.padX}`, lineHeight: '1.2', whiteSpace: 'nowrap', ...stretchOf(density) };
        recap.forEach(([label, value]) => pr.tl({ content: '', colSpan: columns.length - 3 }, { content: label, colSpan: 2, align: 'R', style: plain }, { content: value, align: 'R', style: { ...plain, ...gapOf(columns[columns.length - 1]) } }));
    }
}
/** Signature lines under the document: one label per person, a line below each. */
export function documentSignatures(pr, labels) {
    documentSpacer(pr, 22);
    pr.table({ colNumber: labels.length, html: { style: { width: '100%' } } });
    pr.tl(...labels.map((label) => ({ content: label, align: 'C', style: { fontSize: '11px', padding: '0' } })));
    pr.tl(...labels.map(() => ({ content: '______________________', align: 'C', style: { fontSize: '11px', padding: '26px 0 0' } })));
}
//# sourceMappingURL=DocumentLayout.js.map